import { API_CONFIG } from '../utils/Constants.js';

/**
 * Service for handling API calls
 */
export class ApiService {
    constructor(jwtToken = null, baseUrl = null, mode = 'edit') {
        this.jwtToken = jwtToken;
        this.baseUrl = baseUrl || API_CONFIG.BASE_URL;
        this.mode = mode;

        // Debug logs for ApiService initialization
        console.log('[ApiService] 🔧 Initialized with:', {
            hasJwtToken: !!this.jwtToken,
            jwtToken: this.jwtToken ? `${this.jwtToken.substring(0, 20)}...` : null,
            baseUrl: this.baseUrl,
            mode: this.mode
        });
    }

    /**
     * Get common headers for API requests
     * @returns {Object} Headers object
     */
    getHeaders() {
        const headers = {
            'Content-Type': 'application/json',
        };

        // Add JWT token in Authorization header (edit mode only)
        if (this.jwtToken && this.mode === 'edit') {
            headers['Authorization'] = `Bearer ${this.jwtToken}`;
        }

        // Debug logs
        console.log('[ApiService] 🔑 Request headers:', {
            hasJwtToken: !!this.jwtToken,
            mode: this.mode,
            jwtToken: this.jwtToken ? `${this.jwtToken.substring(0, 20)}...` : null,
            headers: Object.keys(headers)
        });

        return headers;
    }

    /**
     * Handle API response errors
     * @param {Response} response - Fetch response object
     * @throws {Error} Detailed error message
     */
    async handleResponseError(response) {
        if (response.status === 401) {
            throw new Error('Authentication failed: Invalid or expired JWT token');
        }

        if (response.status === 403) {
            throw new Error('Access forbidden: You do not have permission to perform this action');
        }

        // Try to get error message from response body
        let errorMessage = `API error: ${response.status} ${response.statusText}`;
        try {
            const errorData = await response.json();
            if (errorData.message) {
                errorMessage = errorData.message;
            } else if (errorData.error) {
                errorMessage = errorData.error;
            }
        } catch (e) {
            // If we can't parse the error response, use the default message
        }

        throw new Error(errorMessage);
    }

    /**
     * Save virtual tour data to API
     * @param {Object} tourData - Virtual tour data object
     * @param {string} [galleryId] - Optional gallery ID (for updates vs new creation)
     * @returns {Promise<Object>} API response
     */
    async saveVirtualTour(tourData, galleryId = null) {
        let url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.SAVE}`;

        // Add gallery_id as query param if provided (indicates update operation)
        if (galleryId) {
            url += `?gallery_id=${galleryId}`;
            console.log(`📡 Updating experience with gallery_id ${galleryId} at: ${url}`);
        } else {
            console.log(`📡 Creating new experience at: ${url}`);
        }

        console.log(`tourData:`, tourData);

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify(tourData)
        });

        if (!response.ok) {
            await this.handleResponseError(response);
        }

        const data = await response.json();
        console.log('✅ Virtual tour saved successfully:', data);

        return data;
    }

    /**
     * Load virtual tour data from API by gallery ID or UUID
     * @param {string} galleryId - Gallery ID (numeric) or UUID
     * @returns {Promise<Object>} Virtual tour data
     */
    async loadExperience(galleryId) {
        // Check if galleryId is a UUID (contains hyphens) or numeric ID
        const isUuid = galleryId && galleryId.includes('-');

        // Use different endpoint based on mode
        let url;
        if (this.mode === 'view') {
            // View mode: use /view/{gallery_id} endpoint
            url = `${this.baseUrl}/api/v2/galleries-360/view/${galleryId}`;
        } else {
            // Edit mode: use /load?gallery_id={gallery_id} endpoint
            url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.LOAD}?gallery_id=${galleryId}`;
        }

        console.log(`📡 Loading experience from: ${url} (mode: ${this.mode}, ${isUuid ? 'UUID' : 'numeric ID'})`);

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders()
        });

        if (!response.ok) {
            await this.handleResponseError(response);
        }

        const data = await response.json();
        console.log('✅ Experience loaded successfully');

        return data;
    }

    /**
     * Load virtual tour data from API (deprecated, use loadExperience instead)
     * @param {string} tourId - Virtual tour ID
     * @returns {Promise<Object>} Virtual tour data
     * @deprecated Use loadExperience(experienceId) instead
     */
    async loadVirtualTour(tourId) {
        console.warn('[ApiService] loadVirtualTour is deprecated, use loadExperience instead');
        return this.loadExperience(tourId);
    }

    /**
     * Attach a gallery to a project
     * @param {string} galleryId - Gallery ID to attach
     * @param {string} projectId - Project ID to attach to
     * @returns {Promise<Object>} Attach response
     */
    async attachToProject(galleryId, projectId) {
        const url = `${this.baseUrl}/api/v2/galleries-360/${galleryId}/attach`;

        console.log(`📡 Attaching gallery ${galleryId} to project ${projectId} at: ${url}`);

        const response = await fetch(url, {
            method: 'POST',
            headers: this.getHeaders(),
            body: JSON.stringify({ project_id: projectId })
        });

        if (!response.ok) {
            await this.handleResponseError(response);
        }

        const data = await response.json();
        console.log('✅ Gallery attached to project successfully:', data);

        return data;
    }

    /**
     * Validate JWT token and get user information
     * @returns {Promise<Object>} User information
     */
    async validateToken() {
        const url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.AUTH_ME}`;

        console.log(`📡 Validating JWT token at: ${url}`);

        const response = await fetch(url, {
            method: 'GET',
            headers: this.getHeaders()
        });

        if (!response.ok) {
            await this.handleResponseError(response);
        }

        const data = await response.json();
        console.log('✅ Token validated, user info:', {
            id: data.id,
            email: data.email,
            first_name: data.first_name,
            last_name: data.last_name
        });

        return data;
    }

    /**
     * Upload an image file to the API
     * @param {File} file - Image file
     * @returns {Promise<Object>} Upload response with image URL
     */
    async uploadImage(file) {
        const url = `${this.baseUrl}${API_CONFIG.ENDPOINTS.UPLOAD_IMAGE}`;

        console.log(`📡 Uploading image to: ${url}`);

        const formData = new FormData();
        formData.append('image', file);

        const headers = {};

        // Add JWT token in Authorization header (edit mode only)
        if (this.jwtToken && this.mode === 'edit') {
            headers['Authorization'] = `Bearer ${this.jwtToken}`;
        }

        // Note: Don't set Content-Type for FormData, browser will set it automatically with boundary

        const response = await fetch(url, {
            method: 'POST',
            headers: headers,
            body: formData
        });

        if (!response.ok) {
            await this.handleResponseError(response);
        }

        const data = await response.json();
        console.log('✅ Image uploaded successfully:', data);

        return data;
    }
}
